import i18n, { currentLanguage } from '.'
import { KNOWN_DETAILS, KNOWN_DETAIL_PATTERNS } from './apiErrorMap'

const NORWEGIAN_LETTERS = /[æøåÆØÅ]/

/**
 * Localize a FastAPI `detail` for display (#714 AC8).
 *
 * The backend is not touched: it keeps answering in Norwegian, English or
 * "Norsk / English". In Norwegian mode the detail is returned unchanged, so
 * every call site shows exactly what it showed before. In English mode a known
 * Norwegian detail is translated, a bilingual one shows its English half, an
 * English one passes through, and anything else yields undefined so the call
 * site's own (translated) fallback is shown instead of untranslated Norwegian.
 *
 * Use it where a site reads `err.response?.data?.detail`:
 *   setError(localizeApiDetail(err.response?.data?.detail) || t('loadFailed'))
 */
export function localizeApiDetail<T>(detail: T): T | string | undefined {
  if (currentLanguage() === 'no') return detail
  if (typeof detail !== 'string' || !detail) return undefined

  const key = KNOWN_DETAILS[detail]
  if (key) return i18n.t(key, { ns: 'errors' })

  for (const [pattern, patternKey] of KNOWN_DETAIL_PATTERNS) {
    const m = pattern.exec(detail)
    if (m) return i18n.t(patternKey, { ns: 'errors', ...m.groups } as { ns: 'errors' })
  }

  const halves = detail.split(' / ')
  if (halves.length === 2 && !NORWEGIAN_LETTERS.test(halves[1])) return halves[1]

  return NORWEGIAN_LETTERS.test(detail) ? undefined : detail
}
